/**
 * Recuperación del bloqueo tras recargar la página.
 *
 * El widget guarda el `holdToken` en `sessionStorage` (`seats-ai:hold:<eventId>`, valor `{"holdToken":"..."}`) cada vez que
 * el bloqueo se crea o cambia, y lo borra cuando se suelta, vence, se confirma la compra o el servidor dice 404/410. Al
 * arrancar, si hay uno guardado, se pide al servidor (`GET /v1/holds/{token}`) y solo se restaura si sigue vigente.
 *
 * El `holdToken` es una capacidad (quien lo tiene puede leer, ampliar o soltar ese bloqueo): vive únicamente en
 * `sessionStorage` (por pestaña, desaparece al cerrarla) y nunca se pone en la URL de la página, en cookies ni en logs.
 * Todo acceso al almacenamiento va en try/catch: si no existe o lanza, el widget funciona como siempre, sin recuperar.
 */
import { defaultStorage, type StorageLike } from "./session.js";
import type { HoldState } from "./sync.js";
import type { HoldApi, HoldView } from "./types.js";

/** Lo que cabe en un segmento de ruta de la API: no se confía en lo que haya en el almacenamiento. */
const TOKEN_RE = /^[A-Za-z0-9_-]{1,64}$/;

export const holdStorageKey = (eventId: string): string => `seats-ai:hold:${eventId}`;

export interface HoldStore {
  /** token guardado y con formato válido, o null (también si el almacenamiento lanza) */
  load(): string | null;
  save(token: string): void;
  clear(): void;
}

export function createHoldStore(eventId: string, storage: () => StorageLike | null | undefined = defaultStorage): HoldStore {
  const key = holdStorageKey(eventId);
  let last: string | null = null; // lo último guardado: ampliar o extender no reescribe lo mismo
  return {
    load() {
      try {
        const raw = storage()?.getItem(key);
        const token = raw ? (JSON.parse(raw) as { holdToken?: unknown } | null)?.holdToken : null;
        return typeof token === "string" && TOKEN_RE.test(token) ? token : null;
      } catch {
        return null;
      }
    },
    save(token) {
      if (token === last) return;
      try {
        storage()?.setItem(key, JSON.stringify({ holdToken: token }));
        last = token;
      } catch {
        /* sin almacenamiento: no se podrá recuperar tras recargar, y nada más */
      }
    },
    clear() {
      last = null;
      try {
        storage()?.removeItem?.(key);
      } catch {
        /* idem */
      }
    },
  };
}

/** Ciclo de vida: se guarda al crear o cambiar el bloqueo y se borra cuando desaparece (soltado, vencido, comprado, 404/410). */
export const persistHold =
  (store: HoldStore) =>
  (hold: HoldState | null): void => (hold ? store.save(hold.token) : store.clear());

export interface RecoverDeps {
  store: HoldStore;
  api: Pick<HoldApi, "get">;
  eventId: string;
  /** hora estimada del servidor (se usa solo si la respuesta no trae `serverTime`) */
  now: () => number;
}

/**
 * Devuelve el bloqueo guardado si sigue vigente (estado `held`, del mismo evento y sin vencer, con algo reservado), o null.
 * Cualquier otro resultado (comprado, soltado, vencido, otro evento, 404/410, error de red) borra el token guardado y
 * devuelve null: el widget arranca como uno nuevo y sin errores visibles. Nunca lanza. Único caso que conserva el token:
 * el widget se destruyó mientras se esperaba la respuesta.
 */
export async function recoverHold(d: RecoverDeps): Promise<HoldView | null> {
  const token = d.store.load();
  if (!token) return null;
  try {
    const v = await d.api.get(token);
    const now = typeof v.serverTime === "number" ? v.serverTime : d.now();
    const qty = (v.seats?.length ?? 0) + (v.ga ?? []).reduce((n, g) => n + g.qty, 0);
    if (v.status === "held" && v.eventId === d.eventId && v.holdToken === token && v.expiresAt > now && qty > 0) return v;
  } catch (e) {
    if ((e as { code?: unknown } | null)?.code === "aborted") return null;
  }
  d.store.clear();
  return null;
}
